import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { PrenatalIntakeFormData, calculateRiskFactors } from "../PrenatalIntakeFormSchema";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle, AlertTriangle, Info } from "lucide-react";
import { useMemo } from "react";

interface ConsentPreferencesSectionProps {
  form: UseFormReturn<PrenatalIntakeFormData>;
}

const VISIT_LOCATIONS = [
  { value: "home", label: "My home" },
  { value: "community", label: "Community or public location (library, clinic, community center)" },
];

const COMMUNICATION_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "text", label: "Text Message" },
  { value: "both", label: "Both Email and Text" },
];

export function ConsentPreferencesSection({ form }: ConsentPreferencesSectionProps) {
  const formData = form.watch();
  
  // Calculate eligibility
  const riskFactors = useMemo(() => calculateRiskFactors(formData), [formData]);
  
  // Calculate age from date of birth
  const age = useMemo(() => {
    if (!formData.date_of_birth) return null;
    const today = new Date();
    const birthDate = new Date(formData.date_of_birth);
    let calculatedAge = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      calculatedAge--;
    }
    return calculatedAge;
  }, [formData.date_of_birth]);

  const isUnder18 = age !== null && age < 18;
  const hasEnoughRiskFactors = riskFactors >= 4;
  const isEligible = isUnder18 || hasEnoughRiskFactors;

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold border-b pb-2">Eligibility, Consent & Preferences</h3>

      {/* Eligibility Summary */}
      <div className="space-y-4">
        <h4 className="font-medium">PNCC Eligibility Assessment</h4>
        
        {isEligible ? (
          <Alert className="border-primary bg-primary/10">
            <CheckCircle className="h-4 w-4 text-primary" />
            <AlertTitle>Eligible for PNCC Services</AlertTitle>
            <AlertDescription>
              {isUnder18 
                ? `Member is ${age} years old (under 18).`
                : `Member has ${riskFactors} risk factors (4 or more required).`
              }
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="default">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Eligibility Assessment</AlertTitle>
            <AlertDescription>
              Current risk factors: {riskFactors} (4 or more required for eligibility, or under 18 years old)
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Visit Preferences */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium">Visit Location Preferences</h4>
        <Alert variant="default" className="bg-muted">
          <Info className="h-4 w-4" />
          <AlertDescription>
            Home visits are voluntary and may be withdrawn at any time. You may request a different Care Coordinator.
          </AlertDescription>
        </Alert>

        <FormField
          control={form.control}
          name="visit_location_preference"
          render={() => (
            <FormItem>
              <FormLabel>Where would you like visits to take place?</FormLabel>
              <div className="space-y-2 mt-2">
                {VISIT_LOCATIONS.map((location) => (
                  <FormField
                    key={location.value}
                    control={form.control}
                    name="visit_location_preference"
                    render={({ field }) => (
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value?.includes(location.value)}
                            onCheckedChange={(checked) => {
                              const currentValue = field.value || [];
                              if (checked) {
                                field.onChange([...currentValue, location.value]);
                              } else {
                                field.onChange(currentValue.filter((v) => v !== location.value));
                              }
                            }}
                          />
                        </FormControl>
                        <FormLabel className="text-sm font-normal">{location.label}</FormLabel>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Communication Consent */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium">Communication Preferences</h4>

        <FormField
          control={form.control}
          name="communication_consent"
          render={() => (
            <FormItem>
              <FormLabel>How would you like us to communicate with you?</FormLabel>
              <FormDescription>
                This may include appointment reminders, care plans, referrals, and program updates. 
                Electronic communication may include personal health information. You may opt out at any time.
              </FormDescription>
              <div className="space-y-2 mt-2">
                {COMMUNICATION_OPTIONS.map((option) => (
                  <FormField
                    key={option.value}
                    control={form.control}
                    name="communication_consent"
                    render={({ field }) => (
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value?.includes(option.value)}
                            onCheckedChange={(checked) => {
                              const currentValue = field.value || [];
                              if (checked) {
                                field.onChange([...currentValue, option.value]);
                              } else {
                                field.onChange(currentValue.filter((v) => v !== option.value));
                              }
                            }}
                          />
                        </FormControl>
                        <FormLabel className="text-sm font-normal">{option.label}</FormLabel>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Consent Agreement */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium">Consent for Services</h4>
        
        <div className="bg-muted p-4 rounded-lg text-sm space-y-2">
          <p><strong>Voluntary Participation:</strong> I understand that participation in PNCC services is voluntary. I may reduce, end, or transfer services at any time and may request a different Care Coordinator if desired.</p>
          <p><strong>Confidentiality:</strong> I understand that my information is confidential and protected under state and federal law. Information may be shared only with my written consent, except if there is concern of harm to myself or others, suspected abuse or neglect, or as otherwise required by law.</p>
          <p><strong>Consent for Services:</strong> By signing below, I give informed consent for prenatal care coordination services for a period of one year from the date of signature.</p>
        </div>

        <FormField
          control={form.control}
          name="consent_given"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>I agree to the terms and consent to PNCC services *</FormLabel>
              </div>
            </FormItem>
          )}
        />
        <FormMessage>{form.formState.errors.consent_given?.message}</FormMessage>

        <FormField
          control={form.control}
          name="member_signature"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Electronic Signature *</FormLabel>
              <FormDescription>Type your full legal name as your signature</FormDescription>
              <FormControl>
                <Input 
                  placeholder="Your full legal name"
                  className="font-serif italic"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
